export class ApiError extends Error {
  status: number;
  code?: string;
  constructor(status: number, message: string, code?: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

type Reauth = () => Promise<boolean>;
let reauthHandler: Reauth | null = null;

/** The app shell registers the dialog that asks for the password again. */
export function setReauthHandler(fn: Reauth | null) {
  reauthHandler = fn;
}

function messageFrom(body: unknown, status: number): { message: string; code?: string } {
  const d = (body as { detail?: unknown } | null)?.detail;
  if (typeof d === "string") return { message: d };
  if (d && typeof d === "object" && "message" in d) {
    const o = d as { message: string; code?: string };
    return { message: o.message, code: o.code };
  }
  if (Array.isArray(d) && d[0]?.msg) return { message: String(d[0].msg).replace(/^Value error, /, "") };
  return { message: status >= 500 ? "Something went wrong on the server. Try again." : "The request could not be completed." };
}

export async function api<T>(path: string, init: RequestInit & { json?: unknown } = {}, retried = false): Promise<T> {
  const { json, ...rest } = init;
  const headers = new Headers(rest.headers);
  headers.set("x-rai-client", "web");
  if (json !== undefined) headers.set("content-type", "application/json");
  const res = await fetch(path, {
    credentials: "same-origin",
    ...rest,
    headers,
    body: json !== undefined ? JSON.stringify(json) : rest.body,
  });
  if (res.ok) {
    const type = res.headers.get("content-type") || "";
    return (type.includes("application/json") ? res.json() : (res as unknown)) as Promise<T>;
  }
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    /* non-JSON error body */
  }
  const { message, code } = messageFrom(body, res.status);
  if (res.status === 403 && code === "reauth_required" && !retried && reauthHandler) {
    if (await reauthHandler()) return api<T>(path, init, true);
  }
  if (res.status === 401 && !path.startsWith("/api/auth/")) {
    const next = encodeURIComponent(location.pathname + location.search);
    location.assign(`/sign-in?next=${next}`);
  }
  throw new ApiError(res.status, message, code);
}

/** Starts a file download from an API URL without leaving the page. */
export function download(url: string) {
  const a = document.createElement("a");
  a.href = url;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}
